import React, { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  WrenchIcon,
  DownloadIcon,
  SaveIcon,
  PlusIcon,
  XIcon,
  Loader2Icon,
  FolderOpenIcon,
  TrashIcon,
} from 'lucide-react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { exportRowsToCsv } from '../lib/csvExport'
import {
  applyReportFilters,
  getByPath,
  OPERATORS_BY_TYPE,
  type ColumnDef,
  type ColumnType,
  type ComputedFields,
  type FilterRow,
  type Operator,
} from '../lib/reportFilters'
import { buildReportPreset } from '../lib/reportPresets'
import { isDsnyServiceType } from '../lib/serviceTypeOptions'
import {
  MobilePage,
} from '../components/mobile'

interface DataSourceDef {
  label: string
  table: string
  select: string
  columns: ColumnDef[]
  numericColumns: string[]
  /** Derived columns computed client-side from the selected raw fields. */
  computed?: ComputedFields
}

const DATA_SOURCES: Record<string, DataSourceDef> = {
  jobs: {
    label: 'Jobs',
    table: 'jobs',
    select:
      'job_number, title, service_type, status, dsny_integration, scheduled_date, actual_end_time, quoted_amount, actual_amount, materials_cost, garbage_bags_black, garbage_bags_clear, garbage_bags_cost, customer:customers(company_name), property:properties(address), employee:employees(first_name,last_name), crew:crews(name)',
    columns: [
      { key: 'job_number', label: 'Job #', type: 'string' },
      { key: 'title', label: 'Title', type: 'string' },
      { key: 'service_type', label: 'Service Type', type: 'string' },
      { key: 'is_garbage_job', label: 'Garbage (DSNY)', type: 'boolean' },
      { key: 'status', label: 'Status', type: 'string' },
      { key: 'scheduled_date', label: 'Scheduled Date', type: 'date' },
      { key: 'actual_end_time', label: 'Completed At', type: 'date' },
      { key: 'quoted_amount', label: 'Quoted Amount', type: 'number' },
      { key: 'actual_amount', label: 'Actual Amount', type: 'number' },
      { key: 'materials_cost', label: 'Materials Cost', type: 'number' },
      { key: 'garbage_bags_black', label: 'Black Bags', type: 'number' },
      { key: 'garbage_bags_clear', label: 'Clear Bags', type: 'number' },
      { key: 'garbage_bags_cost', label: 'Garbage Bags Cost', type: 'number' },
      { key: 'customer.company_name', label: 'Customer', type: 'string' },
      { key: 'property.address', label: 'Property', type: 'string' },
      { key: 'employee.name', label: 'Employee', type: 'string' },
      { key: 'crew.name', label: 'Crew', type: 'string' },
    ],
    numericColumns: [
      'quoted_amount',
      'actual_amount',
      'materials_cost',
      'garbage_bags_black',
      'garbage_bags_clear',
      'garbage_bags_cost',
    ],
    computed: {
      is_garbage_job: (row) => !!row?.dsny_integration || isDsnyServiceType(row?.service_type),
    },
  },
  invoices: {
    label: 'Invoices',
    table: 'invoices',
    select:
      'invoice_number, invoice_date, due_date, subtotal, tax_amount, total, amount_paid, balance_due, status, customer:customers(company_name)',
    columns: [
      { key: 'invoice_number', label: 'Invoice #', type: 'string' },
      { key: 'invoice_date', label: 'Invoice Date', type: 'date' },
      { key: 'due_date', label: 'Due Date', type: 'date' },
      { key: 'subtotal', label: 'Subtotal', type: 'number' },
      { key: 'tax_amount', label: 'Tax', type: 'number' },
      { key: 'total', label: 'Total', type: 'number' },
      { key: 'amount_paid', label: 'Amount Paid', type: 'number' },
      { key: 'balance_due', label: 'Balance Due', type: 'number' },
      { key: 'status', label: 'Status', type: 'string' },
      { key: 'customer.company_name', label: 'Customer', type: 'string' },
    ],
    numericColumns: ['subtotal', 'tax_amount', 'total', 'amount_paid', 'balance_due'],
  },
  payments: {
    label: 'Payments',
    table: 'payments',
    select:
      'payment_date, amount, payment_method, reference_number, customer:customers(company_name), invoice:invoices(invoice_number)',
    columns: [
      { key: 'payment_date', label: 'Payment Date', type: 'date' },
      { key: 'amount', label: 'Amount', type: 'number' },
      { key: 'payment_method', label: 'Method', type: 'string' },
      { key: 'reference_number', label: 'Reference #', type: 'string' },
      { key: 'customer.company_name', label: 'Customer', type: 'string' },
      { key: 'invoice.invoice_number', label: 'Invoice #', type: 'string' },
    ],
    numericColumns: ['amount'],
  },
  contracts: {
    label: 'Contracts',
    table: 'contracts',
    select:
      'contract_number, title, status, contract_type, total_amount, contract_total_amount, billing_frequency, customer:customers(company_name)',
    columns: [
      { key: 'contract_number', label: 'Contract #', type: 'string' },
      { key: 'title', label: 'Title', type: 'string' },
      { key: 'status', label: 'Status', type: 'string' },
      { key: 'contract_type', label: 'Type', type: 'string' },
      { key: 'total_amount', label: 'Total Amount', type: 'number' },
      { key: 'contract_total_amount', label: 'Contract Total', type: 'number' },
      { key: 'billing_frequency', label: 'Billing Frequency', type: 'string' },
      { key: 'customer.company_name', label: 'Customer', type: 'string' },
    ],
    numericColumns: ['total_amount', 'contract_total_amount'],
  },
  customers: {
    label: 'Customers',
    table: 'customers',
    select:
      'company_name, contact_first_name, contact_last_name, email, phone, billing_city, billing_state, is_active, payment_terms, created_at',
    columns: [
      { key: 'company_name', label: 'Company', type: 'string' },
      { key: 'contact_first_name', label: 'Contact First Name', type: 'string' },
      { key: 'contact_last_name', label: 'Contact Last Name', type: 'string' },
      { key: 'email', label: 'Email', type: 'string' },
      { key: 'phone', label: 'Phone', type: 'string' },
      { key: 'billing_city', label: 'City', type: 'string' },
      { key: 'billing_state', label: 'State', type: 'string' },
      { key: 'is_active', label: 'Active', type: 'boolean' },
      { key: 'payment_terms', label: 'Payment Terms', type: 'number' },
      { key: 'created_at', label: 'Created', type: 'date' },
    ],
    numericColumns: ['payment_terms'],
  },
  properties: {
    label: 'Properties',
    table: 'properties',
    select: 'name, address, city, state, property_type, sqft, is_active, customer:customers(company_name)',
    columns: [
      { key: 'name', label: 'Name', type: 'string' },
      { key: 'address', label: 'Address', type: 'string' },
      { key: 'city', label: 'City', type: 'string' },
      { key: 'state', label: 'State', type: 'string' },
      { key: 'property_type', label: 'Property Type', type: 'string' },
      { key: 'sqft', label: 'Sq Ft', type: 'number' },
      { key: 'is_active', label: 'Active', type: 'boolean' },
      { key: 'customer.company_name', label: 'Customer', type: 'string' },
    ],
    numericColumns: ['sqft'],
  },
  time_entries: {
    label: 'Time Entries',
    table: 'time_entries',
    select:
      'clock_in_time, clock_out_time, total_hours, is_approved, employee:employees(first_name,last_name), job:jobs(job_number,title)',
    columns: [
      { key: 'clock_in_time', label: 'Clock In', type: 'date' },
      { key: 'clock_out_time', label: 'Clock Out', type: 'date' },
      { key: 'total_hours', label: 'Total Hours', type: 'number' },
      { key: 'is_approved', label: 'Approved', type: 'boolean' },
      { key: 'employee.name', label: 'Employee', type: 'string' },
      { key: 'job.job_number', label: 'Job #', type: 'string' },
    ],
    numericColumns: ['total_hours'],
  },
  qc_inspections: {
    label: 'QC Inspections',
    table: 'qc_inspections',
    select:
      'scheduled_date, inspection_date, status, overall_rating, requires_followup, job:jobs(job_number,title)',
    columns: [
      { key: 'scheduled_date', label: 'Scheduled Date', type: 'date' },
      { key: 'inspection_date', label: 'Inspection Date', type: 'date' },
      { key: 'status', label: 'Status', type: 'string' },
      { key: 'overall_rating', label: 'Rating', type: 'number' },
      { key: 'requires_followup', label: 'Needs Follow-up', type: 'boolean' },
      { key: 'job.job_number', label: 'Job #', type: 'string' },
    ],
    numericColumns: ['overall_rating'],
  },
}

interface SavedReport {
  id: string
  name: string
  data_source: string
  columns: string[]
  filters: FilterRow[]
  group_by: string | null
  created_at: string
}

function formatCellValue(value: any, type: ColumnType): string {
  if (value == null) return ''
  if (type === 'boolean') return value ? 'Yes' : 'No'
  if (type === 'number') return Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })
  if (type === 'date') return String(value).slice(0, 10)
  return String(value)
}

export function ReportBuilder() {
  const { user } = useAuth()
  const [searchParams] = useSearchParams()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const preset = useMemo(() => buildReportPreset(searchParams.get('preset')), [])
  const [dataSourceKey, setDataSourceKey] = useState<string>(preset?.dataSource || 'jobs')
  const dataSource = DATA_SOURCES[dataSourceKey]

  const [selectedColumns, setSelectedColumns] = useState<string[]>(
    preset?.columns || dataSource.columns.slice(0, 5).map((c) => c.key)
  )
  const [filters, setFilters] = useState<FilterRow[]>(preset?.filters || [])
  const [groupBy, setGroupBy] = useState<string>(preset?.groupBy || '')
  const [showTotals, setShowTotals] = useState(true)

  const [rows, setRows] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [savedReports, setSavedReports] = useState<SavedReport[]>([])
  const [reportName, setReportName] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    fetchSavedReports()
  }, [])

  const fetchSavedReports = async () => {
    try {
      const { data, error: err } = await supabase
        .from('saved_reports')
        .select('*')
        .order('created_at', { ascending: false })
      if (err) throw err
      setSavedReports(data || [])
    } catch {
      setSavedReports([])
    }
  }

  const handleDataSourceChange = (key: string) => {
    setDataSourceKey(key)
    const next = DATA_SOURCES[key]
    setSelectedColumns(next.columns.slice(0, 5).map((c) => c.key))
    setFilters([])
    setGroupBy('')
    setRows([])
  }

  const toggleColumn = (key: string) => {
    setSelectedColumns((prev) => (prev.includes(key) ? prev.filter((c) => c !== key) : [...prev, key]))
  }

  const addFilter = () => {
    const firstCol = dataSource.columns[0]
    setFilters((prev) => [
      ...prev,
      { column: firstCol.key, operator: OPERATORS_BY_TYPE[firstCol.type][0].value, value: '' },
    ])
  }

  const updateFilter = (index: number, patch: Partial<FilterRow>) => {
    setFilters((prev) => prev.map((f, i) => (i === index ? { ...f, ...patch } : f)))
  }

  const removeFilter = (index: number) => {
    setFilters((prev) => prev.filter((_, i) => i !== index))
  }

  const runQuery = async () => {
    try {
      setLoading(true)
      setError(null)
      const { data, error: err } = await supabase.from(dataSource.table).select(dataSource.select).limit(2000)
      if (err) throw err
      setRows(applyReportFilters(data || [], filters, dataSource.columns, dataSource.computed))
    } catch (err: any) {
      console.error('Error running report:', err)
      setError(err.message || 'Failed to run report')
      setRows([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    runQuery()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSourceKey])

  const displayColumns = useMemo(
    () => dataSource.columns.filter((c) => selectedColumns.includes(c.key)),
    [dataSource, selectedColumns]
  )

  const groupedRows = useMemo(() => {
    if (!groupBy) return null
    const groups = new Map<string, { key: string; count: number; sums: Record<string, number> }>()
    for (const row of rows) {
      const key = String(getByPath(row, groupBy, dataSource.computed) ?? 'N/A')
      const existing = groups.get(key) || { key, count: 0, sums: {} }
      existing.count += 1
      for (const numCol of dataSource.numericColumns) {
        if (!selectedColumns.includes(numCol)) continue
        existing.sums[numCol] = (existing.sums[numCol] || 0) + Number(getByPath(row, numCol, dataSource.computed) || 0)
      }
      groups.set(key, existing)
    }
    return Array.from(groups.values()).sort((a, b) => b.count - a.count)
  }, [groupBy, rows, dataSource.numericColumns, dataSource.computed, selectedColumns])

  const totals = useMemo(() => {
    const sums: Record<string, number> = {}
    for (const col of displayColumns) {
      if (dataSource.numericColumns.includes(col.key)) {
        sums[col.key] = rows.reduce(
          (sum, row) => sum + Number(getByPath(row, col.key, dataSource.computed) || 0),
          0
        )
      }
    }
    return sums
  }, [rows, displayColumns, dataSource.numericColumns, dataSource.computed])

  const handleExportCsv = () => {
    if (groupBy) {
      const groupLabel = dataSource.columns.find((c) => c.key === groupBy)?.label || groupBy
      exportRowsToCsv(
        `${dataSourceKey}-report-grouped.csv`,
        [
          { label: groupLabel, value: (r: any) => r.key },
          { label: 'Count', value: (r: any) => r.count },
          ...dataSource.numericColumns
            .filter((c) => selectedColumns.includes(c))
            .map((c) => ({
              label: dataSource.columns.find((col) => col.key === c)?.label || c,
              value: (r: any) => (r.sums[c] || 0).toFixed(2),
            })),
        ],
        groupedRows || []
      )
    } else {
      exportRowsToCsv(
        `${dataSourceKey}-report.csv`,
        displayColumns.map((c) => ({
          label: c.label,
          value: (r: any) => formatCellValue(getByPath(r, c.key, dataSource.computed), c.type),
        })),
        rows
      )
    }
  }

  const handleSaveReport = async () => {
    if (!reportName.trim()) {
      alert('Please enter a name for this report')
      return
    }
    try {
      setSaving(true)
      const { error: err } = await supabase.from('saved_reports').insert({
        name: reportName.trim(),
        data_source: dataSourceKey,
        columns: selectedColumns,
        filters,
        group_by: groupBy || null,
        created_by: user?.id || null,
      })
      if (err) throw err
      setReportName('')
      await fetchSavedReports()
    } catch (err: any) {
      alert('Failed to save report: ' + (err.message || 'Unknown error'))
    } finally {
      setSaving(false)
    }
  }

  const loadSavedReport = (report: SavedReport) => {
    handleDataSourceChange(report.data_source)
    setTimeout(() => {
      setSelectedColumns(report.columns || [])
      setFilters(report.filters || [])
      setGroupBy(report.group_by || '')
    }, 0)
  }

  const deleteSavedReport = async (id: string) => {
    if (!window.confirm('Delete this saved report?')) return
    await supabase.from('saved_reports').delete().eq('id', id)
    fetchSavedReports()
  }

  return (
    <MobilePage>
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <WrenchIcon className="w-7 h-7 text-blue-600" />
          Custom Report Builder
        </h1>
        <p className="mt-1 text-gray-600">Build, preview, and export custom reports across any data source</p>
      </div>

      {preset && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h2 className="text-sm font-semibold text-blue-900">{preset.name}</h2>
          <p className="text-xs text-blue-800 mt-1">{preset.description}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Configuration panel */}
        <div className="lg:col-span-1 space-y-4">
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <label className="block text-sm font-medium text-gray-700 mb-2">Data Source</label>
            <select
              value={dataSourceKey}
              onChange={(e) => handleDataSourceChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {Object.entries(DATA_SOURCES).map(([key, src]) => (
                <option key={key} value={key}>{src.label}</option>
              ))}
            </select>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <label className="block text-sm font-medium text-gray-700 mb-2">Columns</label>
            <div className="space-y-1.5 max-h-56 overflow-y-auto">
              {dataSource.columns.map((col) => (
                <label key={col.key} className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={selectedColumns.includes(col.key)}
                    onChange={() => toggleColumn(col.key)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  {col.label}
                </label>
              ))}
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">Filters</label>
              <button onClick={addFilter} className="text-blue-600 hover:text-blue-700">
                <PlusIcon className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-2">
              {filters.map((f, index) => {
                const colDef = dataSource.columns.find((c) => c.key === f.column) || dataSource.columns[0]
                const ops = OPERATORS_BY_TYPE[colDef.type]
                return (
                  <div key={index} className="border border-gray-200 rounded-lg p-2 space-y-1.5">
                    <div className="flex items-center gap-1">
                      <select
                        value={f.column}
                        onChange={(e) => {
                          const nextCol = dataSource.columns.find((c) => c.key === e.target.value)
                          updateFilter(index, {
                            column: e.target.value,
                            operator: nextCol ? OPERATORS_BY_TYPE[nextCol.type][0].value : 'contains',
                            value: '',
                          })
                        }}
                        className="flex-1 text-xs px-2 py-1 border border-gray-300 rounded"
                      >
                        {dataSource.columns.map((c) => (
                          <option key={c.key} value={c.key}>{c.label}</option>
                        ))}
                      </select>
                      <button onClick={() => removeFilter(index)} className="text-gray-400 hover:text-red-600">
                        <XIcon className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="flex items-center gap-1">
                      <select
                        value={f.operator}
                        onChange={(e) => updateFilter(index, { operator: e.target.value as Operator })}
                        className="text-xs px-2 py-1 border border-gray-300 rounded"
                      >
                        {ops.map((op) => (
                          <option key={op.value} value={op.value}>{op.label}</option>
                        ))}
                      </select>
                      {colDef.type !== 'boolean' && (
                        <input
                          type={
                            f.operator === 'time_gte' || f.operator === 'time_lte'
                              ? 'time'
                              : colDef.type === 'date'
                              ? 'date'
                              : colDef.type === 'number'
                              ? 'number'
                              : 'text'
                          }
                          value={f.value}
                          onChange={(e) => updateFilter(index, { value: e.target.value })}
                          className="flex-1 text-xs px-2 py-1 border border-gray-300 rounded"
                          placeholder="value"
                        />
                      )}
                    </div>
                  </div>
                )
              })}
              {filters.length === 0 && <p className="text-xs text-gray-400">No filters applied</p>}
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <label className="block text-sm font-medium text-gray-700 mb-2">Group & Totals</label>
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              className="w-full px-3 py-2 mb-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">No grouping</option>
              {dataSource.columns.map((c) => (
                <option key={c.key} value={c.key}>Group by {c.label}</option>
              ))}
            </select>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={showTotals}
                onChange={(e) => setShowTotals(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              Show column totals
            </label>
          </div>

          <div className="flex gap-2">
            <button
              onClick={runQuery}
              disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {loading ? 'Running...' : 'Run Report'}
            </button>
            <button
              onClick={handleExportCsv}
              disabled={rows.length === 0}
              className="flex items-center gap-1.5 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
            >
              <DownloadIcon className="w-4 h-4" />
              CSV
            </button>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <label className="block text-sm font-medium text-gray-700 mb-2">Save Report</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={reportName}
                onChange={(e) => setReportName(e.target.value)}
                placeholder="Report name"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
              <button
                onClick={handleSaveReport}
                disabled={saving}
                className="flex items-center gap-1.5 px-3 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 disabled:opacity-50 text-sm"
              >
                {saving ? <Loader2Icon className="w-4 h-4 animate-spin" /> : <SaveIcon className="w-4 h-4" />}
              </button>
            </div>

            {savedReports.length > 0 && (
              <div className="mt-3 space-y-1 max-h-40 overflow-y-auto">
                {savedReports.map((r) => (
                  <div key={r.id} className="flex items-center justify-between text-xs text-gray-700 px-2 py-1 rounded hover:bg-gray-50">
                    <button onClick={() => loadSavedReport(r)} className="flex items-center gap-1.5 text-left flex-1 truncate">
                      <FolderOpenIcon className="w-3.5 h-3.5 text-gray-400" />
                      {r.name}
                    </button>
                    <button onClick={() => deleteSavedReport(r.id)} className="text-gray-400 hover:text-red-600 ml-2">
                      <TrashIcon className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Preview panel */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-900">
                Preview {groupBy ? `(grouped, ${groupedRows?.length || 0} groups)` : `(${rows.length} rows)`}
              </h2>
              {error && <span className="text-xs text-red-600">{error}</span>}
            </div>

            {loading ? (
              <div className="p-12 text-center">
                <Loader2Icon className="w-8 h-8 animate-spin text-blue-600 mx-auto" />
              </div>
            ) : groupBy && groupedRows ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        {dataSource.columns.find((c) => c.key === groupBy)?.label}
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Count</th>
                      {dataSource.numericColumns
                        .filter((c) => selectedColumns.includes(c))
                        .map((c) => (
                          <th key={c} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            {dataSource.columns.find((col) => col.key === c)?.label}
                          </th>
                        ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {groupedRows.map((g) => (
                      <tr key={g.key} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-sm font-medium text-gray-900">{g.key}</td>
                        <td className="px-4 py-2 text-sm text-gray-900">{g.count}</td>
                        {dataSource.numericColumns
                          .filter((c) => selectedColumns.includes(c))
                          .map((c) => (
                            <td key={c} className="px-4 py-2 text-sm text-gray-900">
                              {(g.sums[c] || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                          ))}
                      </tr>
                    ))}
                    {groupedRows.length === 0 && (
                      <tr><td colSpan={20} className="px-4 py-8 text-center text-gray-400 text-sm">No data</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="overflow-x-auto max-h-[600px]">
                <table className="w-full">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      {displayColumns.map((c) => (
                        <th key={c.key} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                          {c.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {rows.slice(0, 200).map((row, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        {displayColumns.map((c) => (
                          <td key={c.key} className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                            {formatCellValue(getByPath(row, c.key, dataSource.computed), c.type)}
                          </td>
                        ))}
                      </tr>
                    ))}
                    {rows.length === 0 && (
                      <tr><td colSpan={displayColumns.length || 1} className="px-4 py-8 text-center text-gray-400 text-sm">No data</td></tr>
                    )}
                    {showTotals && rows.length > 0 && (
                      <tr className="bg-gray-50 font-semibold">
                        {displayColumns.map((c, i) => (
                          <td key={c.key} className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                            {i === 0 ? 'Total' : dataSource.numericColumns.includes(c.key) ? totals[c.key]?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''}
                          </td>
                        ))}
                      </tr>
                    )}
                  </tbody>
                </table>
                {rows.length > 200 && (
                  <p className="text-xs text-gray-400 px-4 py-2">Showing first 200 of {rows.length} rows. Export CSV for the full dataset.</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </MobilePage>
  )
}
